import { Stack } from '@mui/material';
import { Box } from '@mui/material';
import { Typography } from '@mui/material';
import * as refine from '@refinedev/core';
import { Create, Edit } from '@refinedev/mui';
import { RefinePageHeaderClassNames } from '@refinedev/ui-types';
import { useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { useParams, useNavigate } from 'react-router';
import { BaseField } from '../../../../components/fields/BaseField.tsx';
import { ReadOnlyField } from '../../../../components/fields/ReadOnlyField.tsx';
import { SelectField } from '../../../../components/fields/SelectField.tsx';
import {
  AccountWithMagazines,
  OrganizationWithAccountsAndMagazines,
} from '../../../../types/account.ts';
import { getStatusLabel } from './utils.ts';

const COMPONENT_MAP = { create: Create, edit: Edit } as const;

export const AccountForm = ({ action }: { action: 'create' | 'edit' }) => {
  const { organizationId, id: accountId } = useParams<{ organizationId: string; id: string }>();
  const navigate = useNavigate();
  const API_URL = refine.useApiUrl();

  const { query, formLoading, onFinish } = refine.useForm<AccountWithMagazines>({
    resource: `organization/${organizationId}/accounts`,
    action,
    id: accountId,
  });

  const data = useMemo(
    () => (query?.data?.data ? AccountWithMagazines.parse(query?.data?.data) : undefined),
    [query?.data?.data],
  );

  refine.useCustom<OrganizationWithAccountsAndMagazines>({
    url: `${API_URL}/organization/${organizationId}/dashboard`,
    method: 'get',
    config: {},
    queryOptions: {
      queryKey: ['dashboard'],
      enabled: true,
      onSuccess: (organizationData) => {
        if (action === 'create') {
          const data = OrganizationWithAccountsAndMagazines.parse(organizationData?.data);
          const nbl =
            data.magazines.nbl?.accountLimitNum !== undefined &&
            data.magazines.nbl?.accountCount !== undefined &&
            data.magazines.nbl.accountLimitNum - data.magazines.nbl.accountCount > 0;

          const junkan =
            data.magazines.junkan?.accountLimitNum !== undefined &&
            data.magazines.junkan?.accountCount !== undefined &&
            data.magazines.junkan.accountLimitNum - data.magazines.junkan.accountCount > 0;

          const shiryoban =
            data.magazines.shiryoban?.accountLimitNum !== undefined &&
            data.magazines.shiryoban?.accountCount !== undefined &&
            data.magazines.shiryoban.accountLimitNum - data.magazines.shiryoban.accountCount > 0;

          methods.setValue('magazineOptions.nbl', nbl);
          methods.setValue('magazineOptions.junkan', junkan);
          methods.setValue('magazineOptions.shiryoban', shiryoban);
        }
      },
    },
  });

  const methods = useForm<AccountWithMagazines>({
    values: data,
    defaultValues: {
      magazineOptions: { nbl: false, junkan: false, shiryoban: false },
    },
  });

  const queryClient = useQueryClient();
  const { mutate } = refine.useDelete();

  const handleDelete = () => {
    if (!data?.id) return;
    if (!window.confirm('このユーザーを削除してもよろしいですか？')) return;

    mutate(
      {
        resource: `organization/${organizationId}/accounts`,
        id: data.id,
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ['dashboard'] });
          navigate(`/organization/${organizationId}/accounts`);
        },
      },
    );
  };

  const Component = COMPONENT_MAP[action];

  const status = methods.watch('status');
  const isInvited = methods.watch('isInvited');

  if (status === 'inactive') {
    methods.setValue('magazineOptions.nbl', false);
    methods.setValue('magazineOptions.shiryoban', false);
    methods.setValue('magazineOptions.junkan', false);
  }

  return (
    <Component
      isLoading={formLoading}
      saveButtonProps={{
        disabled: methods.formState.isSubmitting || formLoading,
        onClick: methods.handleSubmit(async (data) => {
          await onFinish(data);
          await queryClient.invalidateQueries({ queryKey: ['dashboard'] });
        }),
      }}
      title={
        <Typography variant="h5" className={RefinePageHeaderClassNames.Title}>
          {action === 'create' ? 'アカウント招待' : 'アカウント編集'}
        </Typography>
      }
      canDelete={true}
      deleteButtonProps={{ onClick: handleDelete }}
    >
      <FormProvider {...methods}>
        <Stack spacing={2}>
          <Box display="flex" gap={2}>
            <BaseField
              name="name"
              rules={{
                required: '必須',
              }}
              label="名前"
              sx={{ flex: 1 }}
            />
            {action === 'create' ? (
              <BaseField
                name="email"
                rules={{
                  required: '必須',
                  validate: (value) => {
                    const result = AccountWithMagazines.shape.email.safeParse(value);
                    if (result.success) {
                      return true;
                    } else {
                      return result.error.issues.at(0)?.message;
                    }
                  },
                }}
                label="メールアドレス"
                sx={{ flex: 1 }}
              />
            ) : (
              <ReadOnlyField name="email" label="メールアドレス" sx={{ flex: 1 }}>
                {data?.email}
              </ReadOnlyField>
            )}
          </Box>
          <Box display="flex" gap={2}>
            <SelectField
              name="magazineOptions.nbl"
              label="NBLオプション"
              options={[true, false]}
              getOptionLabel={(option) => (option ? 'ON' : 'OFF')}
              disableClearable
              sx={{ flex: 1 }}
            />
            <SelectField
              name="magazineOptions.shiryoban"
              label="資料版商事法務オプション"
              options={[true, false]}
              getOptionLabel={(option) => (option ? 'ON' : 'OFF')}
              disableClearable
              sx={{ flex: 1 }}
            />
            <SelectField
              name="magazineOptions.junkan"
              label="旬刊商事法務オプション"
              options={[true, false]}
              getOptionLabel={(option) => (option ? 'ON' : 'OFF')}
              disableClearable
              sx={{ flex: 1 }}
            />
          </Box>
          {action === 'edit' ? (
            <Box display="flex" gap={2}>
              <SelectField
                name="isAdmin"
                label="権限"
                options={[true, false]}
                getOptionLabel={(option) => (option ? '管理者' : '利用者')}
                disableClearable
                sx={{ flex: 1 }}
              />
              <SelectField
                name="status"
                label="利用状況"
                rules={{
                  required: '必須',
                }}
                options={['active', 'inactive']}
                getOptionLabel={(option) => getStatusLabel(option)}
                disableClearable
                sx={{ flex: 1 }}
              />
              <ReadOnlyField label="招待状況" sx={{ flex: 1 }}>
                {isInvited ? '招待中' : '-'}
              </ReadOnlyField>
            </Box>
          ) : null}
        </Stack>
      </FormProvider>
    </Component>
  );
};
